/**
 * What this importer accepts: the image formats Figma will not open itself.
 *
 * Every extension here is read by the ImageMagick build this app ships (its
 * delegates: heic, jxl, raw, openexr, jp2, tiff, webp, and the coders built
 * in for the rest). The README says which were verified with a real file and
 * which rest on the delegate list. PNG, JPEG and SVG are left out on purpose:
 * Figma takes those directly, and a plugin round-trip would only lose quality.
 */

export const MAX_FILE_BYTES = 200 * 1024 * 1024; // a 60 MB RAW decodes to a 100 MB bitmap; wasm memory, not the platform, is the ceiling

export const ACCEPTED_EXTENSIONS = [
  // Phones and the modern web
  '.heic',
  '.heif',
  '.avif',
  '.jxl',
  // Print, scanning and legacy bitmaps
  '.tif',
  '.tiff',
  '.bmp',
  '.tga',
  '.pcx',
  '.pnm',
  '.ppm',
  '.pgm',
  '.ico',
  // Games and film
  '.dds',
  '.exr',
  '.hdr',
  // Editors
  '.xcf',
  // Animations, one frame per artboard
  '.gif',
  '.webp',
  // JPEG 2000
  '.jp2',
  '.j2k',
  // Camera RAW
  '.cr2',
  '.cr3',
  '.nef',
  '.arw',
  '.dng',
  '.raf',
  '.orf',
  '.rw2',
] as const;

interface FileLike {
  name: string;
  size: number;
}

export function validateFile(file: FileLike): string | null {
  const name = file.name.toLowerCase();
  if (!ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext))) {
    return 'Please choose an image Figma cannot open itself — HEIC, AVIF, JPEG XL, TIFF, RAW, TGA, DDS, EXR, XCF, GIF or WebP.';
  }
  if (file.size === 0) return 'That file is empty.';
  if (file.size > MAX_FILE_BYTES) {
    return `That file is too large (max ${MAX_FILE_BYTES / 1024 / 1024} MB).`;
  }
  return null;
}
